import type {Component, Editor} from 'grapesjs';
import {endVersionHistorySession, previewVersion as previewVersionOnCanvas, startVersionHistorySession} from '@Libraries/email-template-version-history.library';

/**
 * Modifier class on the email maker navbar that swaps the options with the version-history controls
 */
const NAVBAR_VERSION_HISTORY_MODE_CLASS = '-version-history-mode';

/**
 * Starts a version-history session: anchors the undo baseline + redo tail, locks the canvas read-only, swaps the navbar to VH controls.
 */
export const enterVersionHistoryMode = (editor: Editor): void => {
  startVersionHistorySession(editor);
  document.getElementById('nav')?.classList.add(NAVBAR_VERSION_HISTORY_MODE_CLASS);
};

/**
 * Applies a stored version as a transient preview, resolving the legacy maker's wrapper handles.
 */
export const previewVersion = (editor: Editor, jsonContent: string): void => {
  previewVersionOnCanvas(editor, jsonContent, {
    wrapperType: window.PMW.emails.components.container.PMW_WRAPPER,
    getMainWrapper: (editorInstance) => editorInstance.runCommand(window.PMW.emails.commands.GET_MAIN_WRAPPER) as Component | undefined,
  });
};

/**
 * Persists the just-restored content: it was applied while autosave was off, so an explicit cache store is needed
 * or the tab-hide beacon flushes the stale cache over it. Call after exitVersionHistoryMode so autosave is back on.
 */
export const persistRestoredTemplate = (editor: Editor): void => {
  void editor.runCommand(window.PMW.emails.commands.SAVE_TO_CACHE);
};

/**
 * Tears down version-history mode: reconciles the redo stack, then restores the canvas, navbar, panel, selection, blocks, and autosave.
 */
export const exitVersionHistoryMode = (editor: Editor): void => {
  endVersionHistorySession(editor);
  window.PMW.emails.IS_IN_VERSION_HISTORY_MODE = false;
  document.getElementById('nav')?.classList.remove(NAVBAR_VERSION_HISTORY_MODE_CLASS);
  editor.runCommand(window.PMW.emails.commands.CLOSE_SIDE_PANEL, {title: window.i18next.t('pmwjs_version_history')});

  const wrapper = editor.runCommand(window.PMW.emails.commands.GET_MAIN_WRAPPER) as Component;
  editor.select(wrapper);

  const blocksManagerContainer = document.getElementById(window.PMW.emails.blocks.ID_BLOCKS_MANAGER_CONTAINER);
  blocksManagerContainer?.classList.remove('_invisible');

  editor.runCommand(window.PMW.emails.commands.SET_AUTOSAVE, {autosave: true});
};
